/**
 * Bill Validation — report figure formulas, in one place.
 *
 * `BillReportScreen.tsx` (what the adviser sees on screen) and the server-side
 * `bvReportContextService` (what the client is emailed) both read from here.
 * That is the whole point of the module: a PDF asserting an overcharge to a
 * paying client must not be able to disagree with the screen it was checked on,
 * and it can't disagree if there is only one implementation of each number.
 *
 * Deliberately dependency-free — no React, no Supabase, no DOM — so the Node
 * server can import it as-is (mirrors `features/scopingAi/lib/*`, which the
 * server already imports the same way).
 */

// ── Formatting ───────────────────────────────────────────────────────────────

const AUD = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

export function fmtCurrency(n: number): string {
  return AUD.format(n);
}

/** Currency with an explicit sign — used wherever a delta is shown. */
export function fmtSignedCurrency(n: number): string {
  return `${n >= 0 ? '+' : ''}${AUD.format(n)}`;
}

export function fmtDate(d: string): string {
  return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Signed variance percentage, or an em dash when the base is $0 (no meaningful %). */
export function fmtVariancePercent(shadow: number, actual: number): string {
  if (shadow === 0) return '—';
  const p = ((actual - shadow) / Math.abs(shadow)) * 100;
  return `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`;
}

// ── Classification ───────────────────────────────────────────────────────────

/**
 * 5-tier traffic light, banded by accuracy % (client spec):
 *   100%       -> perfect (green)
 *   98-99%     -> green
 *   95-97%     -> yellow
 *   <95% over  (retailer charged more) -> red
 *   <95% under (retailer charged less) -> orange
 *
 * A negligible absolute-dollar difference (≤$0.50) always counts as "perfect"
 * regardless of the resulting %, so tiny line items don't get flagged over a
 * rounding-scale variance on a small base amount.
 */
export type VarianceKind = 'perfect' | 'green' | 'yellow' | 'over' | 'under';

/** The match tolerance: a bill within this % of expected is within range. A
 *  screen may widen it (Bill Runs, Dashboard); never below this default. */
export const DEFAULT_MATCH_TOLERANCE_PCT = 5;

/** The tolerances a screen offers, the default first. */
export const MATCH_TOLERANCE_OPTIONS = [DEFAULT_MATCH_TOLERANCE_PCT, 7.5, 10, 15, 20, 25];

/** A tolerance from a URL or input: a number at or above the default, else the default. */
export function matchTolerance(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= DEFAULT_MATCH_TOLERANCE_PCT && n <= 100 ? n : DEFAULT_MATCH_TOLERANCE_PCT;
}

export function classifyVariance(
  shadow: number,
  actual: number,
  tolerancePct: number = DEFAULT_MATCH_TOLERANCE_PCT,
): VarianceKind {
  const d = actual - shadow;
  if (Math.abs(d) <= 0.5) return 'perfect';
  // Shadow $0 with a real retailer charge is NEVER a match — it means the shadow
  // couldn't price this section (missing contract/config) or the retailer charged
  // for something we expected to be free. Either way it needs eyes on it.
  if (shadow === 0) return d > 0 ? 'over' : 'under';
  const accuracy = Math.max(0, (1 - Math.abs(d) / Math.abs(shadow)) * 100);
  if (accuracy >= 99.95) return 'perfect';
  if (accuracy >= 98) return 'green';
  if (accuracy >= 100 - Math.max(tolerancePct, DEFAULT_MATCH_TOLERANCE_PCT)) return 'yellow';
  return d > 0 ? 'over' : 'under';
}

/**
 * Shadow $0: only a $0 actual is 100% — a real charge against an unpriced shadow
 * means the shadow is incomplete (missing configs), not that the bill matches.
 */
export function accuracyPercent(shadow: number, actual: number): number {
  if (shadow === 0) return actual === 0 ? 100 : 0;
  return Math.max(0, (1 - Math.abs(actual - shadow) / Math.abs(shadow)) * 100);
}

/** The accuracy string the customer actually reads — 99.96% reads as "100%". */
export function formatAccuracy(pct: number): string {
  return pct >= 99.95 ? '100%' : `${pct.toFixed(1)}%`;
}

export const VERDICT_LABEL: Record<VarianceKind, string> = {
  perfect: 'MATCHED',
  green:   'MATCHED - WITHIN LIMITS',
  yellow:  'ACCEPTABLE',
  over:    'OVERCHARGED',
  under:   'UNDERCHARGED',
};

/** Short status shown per row in the section comparison table. */
export const ROW_STATUS_LABEL: Record<VarianceKind, string> = {
  perfect: '✓ Matched',
  green:   '✓ Matched',
  yellow:  '≈ Acceptable',
  over:    '↑ Over',
  under:   '↓ Under',
};

export function accuracyCaption(kind: VarianceKind): string {
  if (kind === 'perfect' || kind === 'green') return 'Within acceptable limits';
  if (kind === 'yellow') return 'Acceptable';
  return 'Below acceptable level — under review';
}

// ── Customer-facing copy ─────────────────────────────────────────────────────

/**
 * Verdict blurb shown beside the status badge. Depends on the dollar delta, so
 * it takes the totals rather than just the kind.
 */
export function verdictDescription(kind: VarianceKind, shadowTotal: number, actualTotal: number): string {
  const delta = actualTotal - shadowTotal;
  // A $0 expected total has no meaningful percentage. This used to render
  // "(0.00%)" next to a full-invoice variance — a sentence that reads as "no
  // difference" immediately after stating a several-thousand-dollar one. The
  // rest of the report already prints an em dash in that case.
  const pctAbs = shadowTotal === 0 ? null : Math.abs((delta / shadowTotal) * 100).toFixed(2);
  const magnitude = pctAbs === null
    ? fmtCurrency(Math.abs(delta))
    : `${fmtCurrency(Math.abs(delta))} (${pctAbs}%)`;
  switch (kind) {
    case 'perfect':
      return "We have found your bill to be 100% in line with our calculations. We'll continue to monitor all of your bills every month and if there's any discrepancy outside our tolerance levels, you'll be the first to know.";
    case 'green':
    case 'yellow':
      return 'The variance in your bill is within acceptable limits. We expect any necessary adjustments to appear on your next bill.';
    case 'over':
      return `The Retailer Invoice is ${magnitude} higher than the Expected Invoice. We're looking into this with your retailer.`;
    case 'under':
      return `The Retailer Invoice is ${magnitude} lower than the Expected Invoice.`;
  }
}

/**
 * The Validation Summary paragraph. Traffic-light auto messages per tier —
 * client-specified copy. Yellow (95-97%) currently reuses the green (98-99%)
 * wording pending distinct copy from the client.
 */
export function autoCustomerMessage(kind: VarianceKind, accuracyDisplay: string): string {
  if (kind === 'perfect') {
    return "We have found your bill to be 100% in line with our calculations. We'll continue to monitor all of your bills every month and if there's any discrepancy outside our tolerance levels, you'll be the first to know.";
  }
  if (kind === 'green' || kind === 'yellow') {
    return 'The variance in your bill is within acceptable limits. We expect any necessary adjustments to appear on your next bill. Please reach out if you require further clarification.';
  }
  if (kind === 'under') {
    return `Please see below the latest results of your bill validation. We have found your bill to be at ${accuracyDisplay} accuracy, which is below the acceptable level. The variance on your bill is a potential undercharge. If you would like Choice Energy to follow up on these potential undercharges please email us on shield@choiceenergy.com.au.\n\n*Please Note: The retailer may backdate and recover the undercharge total to your account at a later date.`;
  }
  return `Please see below the latest results of your bill validation. We have found your bill to be at ${accuracyDisplay} accuracy, which is below the acceptable level, and we are looking into these potential overcharges with your retailer. We'll keep you updated on any developments.`;
}

export const MANUAL_NOTES: Record<string, string> = {
  overcharge:  'We are investigating a potential overcharge with the retailer.',
  undercharge: 'The variance on your bill is a potential undercharge. If you would like Choice Energy to follow up on these potential undercharges please email us on shield@choiceenergy.com.au.\n\n*Please Note: The retailer may backdate and recover the undercharge total to your account at a later date.',
  gst:         'The GST amount on your bill may differ from what we initially expected, often due to recent adjustments. Thank you for your understanding.',
};

export const VALIDATION_STATUS_COPY: Record<string, { title: string; body: string | null }> = {
  validated_approved: {
    title: 'Validated and Approved',
    body: 'A discrepancy was detected by the system, but after manually verifying the bill, all appears accurate.',
  },
  discrepancy_detected: {
    // Body is the auto message — supplied by the caller, which is why it is null here.
    title: 'Discrepancy Detected',
    body: null,
  },
  missing_invoice: {
    title: 'Invoice Not Received',
    body: 'We have not received your invoice this month. Please forward your latest bill to shield@choiceenergy.com.au so we can complete your validation.',
  },
};

export const DISCLAIMER_PARAGRAPHS = [
  'Choice Energy ("CE") utilises available contract information, market data, meter data and billing information to validate electricity and gas rates, usage charges and other applicable billing components. This is listed under expected Invoice Total.',
  'Certain non-standard charges, including but not limited to interest charges, retrospective adjustments, pass-through charges, regulatory fees, industry charges and other miscellaneous billing items, may not be validated as part of the bill validation process.',
  'Environmental and sustainability-related charges are subject to change and may vary between billing periods. Variances in these charges do not necessarily indicate a billing error.',
  'CE periodically updates and enhances its bill validation systems, methodologies and processes. As a result, the scope, functionality and format of bill validation outcomes may change over time.',
  'Prior to notifying a retailer of a potential billing discrepancy, CE will undertake a secondary review to verify the identified issue and obtain customer approval before proceeding with any claim or dispute.',
  'CE will generally pursue bill credits or debits only where the aggregate value of the identified billing discrepancy exceeds $300 (exclusive of GST, where applicable).',
  'While CE takes reasonable care in performing bill validation services, CE does not warrant that all billing errors, discrepancies or anomalies will be identified. CE shall not be liable for any billing errors, overcharges, undercharges or other discrepancies that are not detected, identified or reported through the bill validation process.',
];

export const COMPANY_FOOTER = {
  address: 'Choice Energy Pty Ltd, Level 6, 580 St Kilda Road Melbourne VIC 3004',
  registration: 'ABN: 64 935 373 830    ACN No: 166 176 325',
  website: 'www.choiceenergy.com.au',
};

// ── Section rows ─────────────────────────────────────────────────────────────

export type ReportSectionKey = 'retail' | 'distribution' | 'metering' | 'environmental' | 'other';

// Other is listed because the Comparison tab has it: the report's rows are the
// Comparison's sections (see comparisonSections.ts), and leaving one out meant
// the rows did not add up to the subtotal beneath them.
export const REPORT_SECTIONS: { key: ReportSectionKey; label: string }[] = [
  { key: 'retail',        label: 'Retail Energy' },
  { key: 'distribution',  label: 'Network / Distribution' },
  { key: 'metering',      label: 'Metering' },
  { key: 'environmental', label: 'Environmental' },
  { key: 'other',         label: 'Other' },
];

/**
 * Which comparison rows the report shows. Environmental is suppressed for sites
 * without GreenPower, and any section that is $0 on both sides is dropped —
 * both rules live here so the emailed table has exactly the rows the screen does.
 */
export function visibleSections(
  shadowTotals: Partial<Record<string, number>>,
  actualTotals: Partial<Record<string, number>>,
  hasGreenPower: boolean,
): { key: ReportSectionKey; label: string; shadow: number; actual: number }[] {
  return REPORT_SECTIONS.flatMap(({ key, label }) => {
    if (key === 'environmental' && !hasGreenPower) return [];
    const shadow = shadowTotals[key] ?? 0;
    const actual = actualTotals[key] ?? 0;
    if (shadow === 0 && actual === 0) return [];
    return [{ key, label, shadow, actual }];
  });
}

// ── Field resolution ─────────────────────────────────────────────────────────

type AddressParts = {
  addr_street?: string | null;
  addr_city?: string | null;
  addr_state?: string | null;
  addr_postcode?: string | null;
};

export function buildSiteAddress(site: AddressParts | null | undefined): string {
  if (!site) return '—';
  const parts = [site.addr_street, site.addr_city, site.addr_state, site.addr_postcode].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : '—';
}

/**
 * The retailer name shown on the report.
 *
 * Order matters: an explicitly passed name wins, then the retailer the invoice
 * parser actually read off the invoice, then the retailer recorded against the
 * site. The report screen used to take only the (never-supplied) prop and so
 * printed "—" even when both fallbacks were populated.
 */
export function resolveRetailerName(
  explicit: string | null | undefined,
  extractedRetailer: string | null | undefined,
  siteRetailer: string | null | undefined,
): string {
  return explicit || extractedRetailer || siteRetailer || '—';
}
